import { useState } from 'react';

import type { UpdatePrivateEventInput } from '../../api/private-event-update';
import type { PrivateEventDetail } from '../../api/privateEvents';

import { CHECK_FIELDS_MESSAGE, saveSection } from './private-event-section-save';
import type { StepErrors } from './private-event-validation';
import { useFieldErrors } from './use-field-errors';
import { useUpdatePrivateEvent } from './use-update-private-event';

interface Config<D extends object> {
  event: PrivateEventDetail;
  seed: (event: PrivateEventDetail) => D;
  patch: (seed: D, draft: D) => UpdatePrivateEventInput;
  validate?: (event: PrivateEventDetail, draft: D) => StepErrors;
  /** Error keys that go stale when a draft field is edited, beyond its own. */
  related?: Partial<Record<keyof D, string[]>>;
  onDenied: () => void;
}

/**
 * A section's form state. It seeds when editing starts (`begin`), never on a
 * refetch, validates, then sends only what changed.
 */
export function useSectionDraft<D extends object>(config: Config<D>) {
  const { event, seed, patch, validate, related, onDenied } = config;
  const [seeded, setSeeded] = useState(() => seed(event));
  const [draft, setDraft] = useState(seeded);
  const { setErrors, fieldError, clear } = useFieldErrors();
  const update = useUpdatePrivateEvent(event.id);

  const begin = () => {
    const fresh = seed(event);
    setSeeded(fresh);
    setDraft(fresh);
    setErrors({});
  };

  const set = <K extends keyof D>(key: K, value: D[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    clear(String(key), ...(related?.[key] ?? []));
  };

  /** `effective` stands in for the draft when the form derives part of it. */
  const submit = async (effective: D = draft) => {
    const errs = validate?.(event, effective) ?? {};
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      throw new Error(CHECK_FIELDS_MESSAGE);
    }
    await saveSection({
      patch: patch(seeded, effective),
      mutate: update.mutateAsync,
      setFieldErrors: setErrors,
      onDenied,
    });
  };

  return { draft, set, setDraft, begin, submit, fieldError, clear, setErrors };
}
